import { CommonModule, KeyValue } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import {
  AppG2CandlestickChartComponent,
  EntityConfigInterface,
  RappiderAvatarModule,
  RappiderButtonGroupModule,
  RappiderButtonModule,
  RappiderHeadingModule,
  RappiderIconModule,
  RappiderInputGroupModule,
  RappiderInputLabelModule,
  RappiderListGridModule,
  RappiderParagraphModule,
  RappiderAlertModule,
} from '@rappider/rappider-components';
import {
  AlertTypes,
  AvatarComponentConfig,
  ButtonComponentConfig,
  ButtonSize,
  ButtonType,
  defaultInputGroupConfig,
  defaultReceiverAvatarConfig,
  defaultSenderAvatarConfig,
  HeadingType,
  MessageType,
  PipesModule,
} from '@rappider/rappider-components/utils';
import { RappiderMarkdownViewerModule } from 'libs/components/src/lib/components/markdown-viewer/markdown-viewer.module';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { RappiderJsonViewerComponent } from '../../../../../components/src/lib/components/json-viewer/json-viewer.component';

import { CompiledComponentRendererComponent } from '../../../../../content-tree-renderer/src/lib/components/compiled-component-renderer/compiled-component-renderer.component';
import { Store } from '@ngrx/store';
import { AiFunctionCallControllerService, ProjectChannelMessage, WorkflowManagementControllerService } from '@rappider/rappider-sdk';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { debounceTime, fromEvent } from 'rxjs';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { RappiderChartBaseModule } from 'libs/components/src/lib/components/chart-base/chart-base.module';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ContentTreeRendererModule } from '@rappider/content-tree-renderer';
import { RouterModule } from '@angular/router';
import { LoopbackCrudApiService } from '../../services/loopback-crud-api.service';

import { UIComponentDataProviderService } from '../../services/ui-component-data-provider.service';
import { AIResponseRendererComponent } from '../ai-response-renderers/ai-response-renderer.component';

@Component({
  selector: 'rappider-chat-messages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RappiderInputGroupModule,
    NzButtonModule,
    RappiderAvatarModule,
    NzSkeletonModule,
    PipesModule,
    RappiderButtonModule,
    RappiderButtonGroupModule,
    RappiderMarkdownViewerModule,
    NzTimelineModule,
    NzTagModule,
    RappiderJsonViewerComponent,
    CompiledComponentRendererComponent,
    NzSpinModule,
    NzToolTipModule,
    RappiderListGridModule,
    NzCollapseModule,
    RappiderChartBaseModule,
    AppG2CandlestickChartComponent,
    ContentTreeRendererModule,
    RouterModule,
    AIResponseRendererComponent,
    RappiderHeadingModule,
    RappiderInputLabelModule,
    RappiderIconModule,
    RappiderParagraphModule,
    RappiderAlertModule
  ],
  templateUrl: './chat-messages.component.html',
  styleUrls: ['./chat-messages.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({
        height: '*',
        opacity: 1,
        transform: 'translateY(0)'
      })),
      state('out', style({
        height: '0px',
        opacity: 0,
        transform: 'translateY(-10px)'
      })),
      transition('in => out', animate('400ms cubic-bezier(0.4, 0, 0.2, 1)')),
      transition('out => in', animate('400ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ]),
    trigger('rotateIcon', [
      state('up', style({
        transform: 'rotate(0deg)'
      })),
      state('down', style({
        transform: 'rotate(180deg)'
      })),
      transition('up => down', animate('300ms cubic-bezier(0.4, 0, 0.2, 1)')),
      transition('down => up', animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(20px)'
        }),
        animate('500ms cubic-bezier(0.4, 0, 0.2, 1)', style({
          opacity: 1,
          transform: 'translateY(0)'
        }))
      ])
    ])
  ]
})
export class RappiderChatMessagesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() messages: ProjectChannelMessage[] | undefined;
  @Input() entityConfigs?: { [key: string]: EntityConfigInterface } = {};
  @Input() channelId?: string | undefined;
  @Input() isTyping = false;
  @Input() senderAvatar: AvatarComponentConfig | undefined;
  @Input() receiverAvatar: AvatarComponentConfig | undefined;
  @Input() sendButtonDisabled = false;
  @Input() displaySkeleton = true;
  @Input() fullScreenButtonVisibility = false;
  @Input() quickAnswerOptions?: KeyValue<string, string>[];
  @Input() isLoadingPreviousMessages = false;

  @Input() previousMessage?: ProjectChannelMessage | undefined;
  @Input() nextMessage?: ProjectChannelMessage | undefined;

  @Output() messageSent = new EventEmitter<string>();
  @Output() messageButtonClick = new EventEmitter<ProjectChannelMessage>();
  @Output() fullScreenButtonClick = new EventEmitter<ProjectChannelMessage>();
  @Output() quickAnswerButtonClick = new EventEmitter<string>();
  @Output() scrolledToTop = new EventEmitter<string>();
  @Output() responseAction = new EventEmitter<{ type: string; data: any }>();
  @Output() splitterToggleRequested = new EventEmitter<boolean>();
  @Output() revertRequested = new EventEmitter<{ messageId: string; draftId: string; stepId: string }>();

  @ViewChild('chatWrapper') private chatWrapper: ElementRef | undefined;
  @ViewChildren('messageContentWrapper') messageContentWrappers: QueryList<ElementRef> | undefined;

  inputGroupConfig = defaultInputGroupConfig;
  inputValue = '';
  MessageType = MessageType;
  AlertTypes = AlertTypes;
  HeadingType = HeadingType;
  quickAnswerButtons: ButtonComponentConfig[] = [];
  isAITyping = false;

  scrollHeightBeforeLoad: number | undefined;
  messageDetailsCollapsed = false;
  isAdditionalMessagesCollapseVisible = false;

  /* We save the last message of the message array so that when new messages are loaded, we can know if there is a new or previous message */
  previousLatestMessages: ProjectChannelMessage[] | undefined;

  aiSenderId = '00000000-0000-0000-0000-000000000001';
  private filePreviewUrls = new Map<File, string>();

  constructor(
    private store: Store<any>,
    private loopbackCrudApiService: LoopbackCrudApiService,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
    private workflowManagementControllerService: WorkflowManagementControllerService,
    private uiComponentDataProviderService: UIComponentDataProviderService
  ) { }

  ngOnInit(): void {
    this.initDefaults();
  }

  ngAfterViewInit(): void {
    if (this.chatWrapper) {
      fromEvent(this.chatWrapper.nativeElement, 'scroll')
        .pipe(debounceTime(200))
        .subscribe(() => {
          if (this.chatWrapper?.nativeElement?.scrollTop === 0 && this.messages?.length) {
            this.scrolledToTop.emit(this.messages[0].createdDate);
          }
        });
    }

    // // This subscription will fire whenever new message content is rendered to the DOM.
    // this.messageContentWrappers.changes.subscribe(() => {
    //   this.messages?.forEach((message, index) => {
    //     if (message['isVisible'] && !message['renderedHeight']) {
    //       const uniqueId = message.id || message._id || message.name || index.toString();
    //       const elementRef = this.messageContentWrappers.find(
    //         (el) => el.nativeElement.id === uniqueId
    //       );
    //       if (elementRef) {
    //         message['renderedHeight'] = elementRef.nativeElement.offsetHeight;
    //       }
    //     }
    //   });
    // });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['messages'] && this.chatWrapper) {
      if (changes['messages']?.currentValue?.length && changes['messages']?.previousValue?.length) {
        const currentLastMessage = changes['messages'].currentValue.slice(-1)[0];
        const isRecentMessage = this.isRecentMessage(currentLastMessage?.createdDate);
        // Only scroll for user messages, not AI responses
        if (isRecentMessage && currentLastMessage?.senderId !== this.aiSenderId) {
          setTimeout(() => this.scrollToBottom(true), 200);
        }

        /* This code is commented out to avoid unnecessary scrolling when loading previous messages
                setTimeout(() => {
          if (this.scrollHeightBeforeLoad) {
            this.chatWrapper.nativeElement.scrollTop =
              this.chatWrapper?.nativeElement.scrollHeight - this.scrollHeightBeforeLoad;
          }
        }, 0);
        */
      }
    }

    if (changes['isTyping'] && changes['isTyping'].currentValue === true) {
      setTimeout(() => this.scrollToBottom(true), 100);
    }

    if (changes.senderAvatar || changes.receiverAvatar) {
      this.initDefaults();
    }

    if (changes.textboxDisabled && this.inputGroupConfig?.suffixButton) {
      this.inputGroupConfig.suffixButton.disabled = this.sendButtonDisabled;
    }

    if (changes.quickAnswerOptions) {
      this.quickAnswerButtons =
        this.quickAnswerOptions?.map((option) => ({
          key: option.value,
          text: option.key,
          type: ButtonType.Default,
          block: false,
          size: ButtonSize.Small,
          borderSettings: {
            borderRadius: '0px',
          },
        })) || [];
    }
  }

  /**
   * This method is triggered when a message placeholder becomes visible.
   * It sets the 'isVisible' flag to true, which triggers the rendering of the actual message content.
   */
  // onMessageVisible(message: ProjectChannelMessage) {
  //   if (!message['isVisible']) {
  //     message['isVisible'] = true;
  //   }
  // }

  initDefaults() {
    if (!this.senderAvatar) {
      this.senderAvatar = defaultSenderAvatarConfig;
    }
    if (!this.receiverAvatar) {
      this.receiverAvatar = defaultReceiverAvatarConfig;
    }
  }

  onScroll() {
    if (
      this.chatWrapper?.nativeElement.scrollTop === 0 &&
      this.messages &&
      this.messages.length > 0
    ) {
      this.scrollHeightBeforeLoad = this.chatWrapper?.nativeElement.scrollHeight;
      this.scrolledToTop.emit(this.messages[0].createdDate);
    }
  }

  scrollToBottom(isImmediate: boolean = false) {
    const element = document.getElementById('bottom-anchor');
    if (element) {
      element.scrollIntoView({ behavior: isImmediate ? 'auto' : 'smooth' });
    }
  }

  onEnterClick() {
    if (
      !this.sendButtonDisabled &&
      this.inputValue !== '' &&
      this.inputValue !== null &&
      this.inputValue !== undefined
    ) {
      this.messageSent.emit(this.inputValue);
      this.inputValue = '';
    }
  }

  onButtonClick() {
    if (
      !this.sendButtonDisabled &&
      this.inputValue !== '' &&
      this.inputValue !== null &&
      this.inputValue !== undefined
    ) {
      this.messageSent.emit(this.inputValue);
      this.inputValue = '';
    }
  }

  /* this function avoids re-rendering the list unnecessarily */
  trackMessages(index: any, item: any) {
    return item?.id || item?._id || item?.name || index;
  }

  onMessageButtonClick(message: ProjectChannelMessage) {
    this.messageButtonClick.emit(message);
  }

  onContinueMessageButtonClick(messageContent: string) {
    this.messageSent.emit(messageContent);
  }

  onFullScreenButtonClick() {
    this.fullScreenButtonClick.emit();
  }

  onQuickAnswerButtonClick(button: ButtonComponentConfig) {
    this.quickAnswerButtonClick.emit(button.key);
  }

  onRealtimeTypingInProgress(timestamp: string) {
    this.isAITyping = true;
    setTimeout(() => {
      this.scrollToBottom(true);
    }, 50);
  }

  onRealtimeTypingCompleted(message: ProjectChannelMessage) {
    this.isAITyping = false;
    if (this.channelId) {
      this.store.dispatch({
        type: '[AIAssistant] RealtimeTypingCompleted',
        payload: {
          channelId: this.channelId,
          message: message,
        },
      });
    }
  }

  isRecentMessage(timestamp: string): boolean {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const sixtySecondsAgo = new Date(now.getTime() - 60 * 1000);
    return messageDate >= sixtySecondsAgo;
  }

  public isString(value: any): boolean {
    return typeof value === 'string';
  }

  public isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  public getFilePreviewUrl(file: File): string {
    if (!this.filePreviewUrls.has(file)) {
      const url = URL.createObjectURL(file);
      this.filePreviewUrls.set(file, url);
    }
    return this.filePreviewUrls.get(file) || '';
  }

  public getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'fa fa-file-pdf';
      case 'doc':
      case 'docx': return 'fa fa-file-word';
      case 'xls':
      case 'xlsx': return 'fa fa-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fa fa-file-image';
      case 'txt':
      case 'md': return 'fa fa-file-text';
      case 'json':
      case 'xml': return 'fa fa-file-code';
      default: return 'fa fa-file';
    }
  }

  public previewFile(file: File): void {
    if (this.isImageFile(file)) {
      const url = this.getFilePreviewUrl(file);
      window.open(url, '_blank');
    }
  }

  public getImageFiles(files: File[]): File[] {
    return files.filter(file => this.isImageFile(file));
  }

  public getNonImageFiles(files: File[]): File[] {
    return files.filter(file => !this.isImageFile(file));
  }

  /**
   * Get files to display (max 5 items)
   * @param files - Array of files
   * @returns Array of files limited to 5 items
   */
  public getDisplayFiles(files: File[]): File[] {
    return files.slice(0, 5);
  }

  /**
   * Get shortened file name for display
   * @param fileName - Full file name
   * @returns Shortened file name
   */
  public getShortFileName(fileName: string): string {
    const maxLength = 12;
    if (fileName.length <= maxLength) {
      return fileName;
    }
    const extension = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const shortName = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
    return shortName + '.' + extension;
  }

  /**
   * Get names of hidden files for tooltip
   * @param files - Array of files
   * @returns Newline-separated string of hidden file names
   */
  public getHiddenFileNamesTooltip(files: File[]): string {
    if (files.length <= 5) {
      return '';
    }
    const hiddenFiles = files.slice(5);
    return hiddenFiles.map(file => file.name).join('\n');
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  ngOnDestroy(): void {
    // Clean up blob URLs to prevent memory leaks
    this.filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    this.filePreviewUrls.clear();
  }

  onMessageDataEntityItemSelected(item: any) {
    console.log('Selected Entity Item:', item);
  }

  getEntityConfigByName(entityName: string): EntityConfigInterface | undefined {
    if (this.entityConfigs?.[entityName]) {
      return this.entityConfigs[entityName];
    } else {
      return undefined;
    }
  }


  onRecommendAppointmentButtonClicked(message: ProjectChannelMessage) {
    console.log('Recommend Appointment Button Clicked:', message);
  }

  getChartConfig(message: any): any {
    if (message?.data?.chart?.config && message?.data?.chart?.data) {
      return <any>{
        ...message.data.chart.config,
        data: message.data.chart.data
      };
    }
    return {};
  }


  /**
   * Handle response actions from AI response renderers
   * @param event - Response action event
   */
  onResponseAction(event: { type: string; data: any }): void {
    console.log('AI response action:', event);
    this.responseAction.emit(event);
  }

  /**
   * Handle splitter toggle requests from AI response renderers
   * @param shouldOpen - Whether to open the splitter
   */
  onSplitterToggleRequested(shouldOpen: boolean): void {
    this.splitterToggleRequested.emit(shouldOpen);
  }


  /**
   * Handle revert requests from AI response renderer
   */
  onRevertRequested(event: { messageId: string; draftId: string; stepId: string }): void {
    this.revertRequested.emit(event);
  }

  /**
   * Toggle message details collapse state
   */
  toggleMessageDetailsCollapse(): void {
    this.messageDetailsCollapsed = !this.messageDetailsCollapsed;
  }

  /**
   * Handle retry generation button click
   */
  onRetryGeneration(): void {
    // Emit a retry event that can be handled by parent components
    this.responseAction.emit({ type: 'retry_generation', data: null });
  }

  onAdditionalMessageVisibilityChange() {
    this.isAdditionalMessagesCollapseVisible = !this.isAdditionalMessagesCollapseVisible;
  }

  /**
 * Check if message is an error message
 * Error messages have responseData as null and are from AI sender
 * @param message - Message to check
 * @returns True if message is an error message
 */
  isErrorMessage(message: ProjectChannelMessage): boolean {
    return (
      message.senderId === this.aiSenderId &&
      message.responseData === null &&
      message.type === MessageType.AI &&
      !!message.message
    );
  }

}
